import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import { whyChooseItems, comparisonData, resourceStats, seoArticleCards, faqItems } from '@/mocks/homeData';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.boats';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'VigorBuy Spreadsheet Resources',
  url: siteUrl,
  description: 'Complete resource hub for VigorBuy Spreadsheet with guides, templates, reviews, comparisons, and tutorials.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'VigorBuy Spreadsheet',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg',
  description: 'VigorBuy Spreadsheet provides comprehensive spreadsheet solutions with professional templates, automation features, and smart data organization tools.',
};

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />
      <Navbar />
      <main>
        <HeroSection />

        {/* Trusted Resource Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                Trusted Resource For Spreadsheet Users
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                Thousands of users rely on our comprehensive guides, templates, and tutorials to master VigorBuy Spreadsheet.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-file-copy-2-line', title: 'Templates', desc: 'Professional ready-to-use spreadsheet templates for every business need' },
                { icon: 'ri-book-3-line', title: 'Guides', desc: 'Step-by-step guides covering everything from basics to advanced techniques' },
                { icon: 'ri-play-circle-line', title: 'Tutorials', desc: 'Video and written tutorials to help you get the most out of your spreadsheets' },
                { icon: 'ri-scales-3-line', title: 'Comparisons', desc: 'Honest side-by-side comparisons with other spreadsheet solutions' },
              ].map((item) => (
                <div key={item.title} className="group p-6 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-200 transition-all duration-300 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <CategorySection />

        {/* What Is VigorBuy Spreadsheet Section */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <p className="text-sm font-semibold text-accent-500 uppercase tracking-wider mb-3">About</p>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-6">
                  What Is VigorBuy Spreadsheet?
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">VigorBuy Spreadsheet</strong> is a structured 
                    spreadsheet solution designed for organizing information, improving productivity, and 
                    simplifying data workflows. Unlike traditional spreadsheet tools that require extensive 
                    manual setup, VigorBuy Spreadsheet provides ready-to-use templates and smart 
                    organizational features that help users hit the ground running.
                  </p>
                  <p>
                    Whether you are managing business finances, tracking inventory, organizing sales data, 
                    or planning projects, VigorBuy Spreadsheet offers the tools and resources needed to work 
                    smarter. The platform combines the flexibility of spreadsheets with the power of modern 
                    automation, making it an ideal choice for both beginners and experienced professionals.
                  </p>
                  <p>
                    With a growing library of guides, tutorials, and templates, VigorBuy Spreadsheet has 
                    become a trusted resource for individuals and businesses looking to optimize their data 
                    management workflows. The comprehensive resource collection ensures users can find 
                    exactly what they need, when they need it.
                  </p>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Explore VigorBuy Spreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <Link
                    to="/blog/what-is-vigorbuy-spreadsheet-complete-guide"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Read Full Guide
                    <i className="ri-book-open-line"></i>
                  </Link>
                </div>
              </div>
              <div className="flex-1 w-full max-w-md lg:max-w-none">
                <div className="relative rounded-2xl overflow-hidden shadow-sm aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20desk%20setup%20with%20laptop%20displaying%20clean%20organized%20spreadsheet%20interface%20with%20charts%20and%20data%20tables%2C%20ceramic%20coffee%20cup%20on%20wooden%20coaster%2C%20small%20potted%20succulent%20plant%2C%20warm%20natural%20window%20light%20from%20left%20side%2C%20soft%20beige%20and%20cream%20color%20palette%2C%20cozy%20productive%20home%20office%20atmosphere%2C%20editorial%20photography%20style%20with%20shallow%20depth%20of%20field&width=800&height=600&seq=whatis-vigorbuy-v2&orientation=landscape"
                    alt="VigorBuy Spreadsheet workspace setup"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Comparison Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-3">Comparison</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                VigorBuy Spreadsheet vs Traditional Spreadsheets
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                See how VigorBuy Spreadsheet compares to traditional spreadsheet solutions across key features.
              </p>
            </div>
            <div className="rounded-2xl border border-background-200/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-700">Feature</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-primary-600">VigorBuy Spreadsheet</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-600">Traditional Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                        <td className="px-6 py-3.5 text-sm text-foreground-700 font-medium">{row.feature}</td>
                        <td className="px-6 py-3.5 text-sm text-primary-600 font-semibold">
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-checkbox-circle-fill text-xs"></i>
                            {row.vigorbuy}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-sm text-foreground-500">{row.traditional}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-accent-500 uppercase tracking-wider mb-3">Benefits</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                Why Choose VigorBuy Spreadsheet
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                Discover the key benefits that make VigorBuy Spreadsheet the preferred choice for data management.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {whyChooseItems.map((item) => (
                <div key={item.title} className="group p-6 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors mb-4">
                    <i className={`${item.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Articles Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-3">Knowledge Hub</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                VigorBuy Spreadsheet Articles &amp; Resources
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                Dive deep into our collection of original, in-depth articles covering every aspect of VigorBuy Spreadsheet. Expert insights, practical guides, and valuable tips.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {seoArticleCards.map((article) => (
                <Link
                  key={article.title}
                  to={article.link}
                  className="group flex flex-col rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="inline-block w-fit px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                      {article.label}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed flex-1 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                      <span>Read Article</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Resource Library Section */}
        <section className="py-20 md:py-28 bg-accent-500">
          <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
              Your Complete Resource Library
            </h2>
            <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-12">
              Access everything you need to become a VigorBuy Spreadsheet expert. Our growing library of resources
              ensures you are always equipped with the latest knowledge and tools.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
              {resourceStats.map((stat) => (
                <div key={stat.label} className="p-6 rounded-xl bg-white/10 backdrop-blur-sm">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.count}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link
              to="/blog/what-is-vigorbuy-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-accent-600 text-base font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Resources
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Security Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-3">Security</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                Is VigorBuy Spreadsheet Safe?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                We prioritize your security and privacy. Here is how VigorBuy Spreadsheet ensures a safe experience.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: 'ri-lock-line',
                  title: 'Secure Browsing',
                  desc: 'All connections to VigorBuy Spreadsheet resources use encrypted protocols, ensuring your browsing activity remains private and protected from unauthorized access.',
                },
                {
                  icon: 'ri-eye-off-line',
                  title: 'Privacy Protection',
                  desc: 'Your personal information is handled with the utmost care. We follow strict privacy guidelines to ensure your data is never shared without your consent.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Safe Resource Access',
                  desc: 'Every guide, template, and tutorial available through our platform is verified and safe to use. We regularly review all resources for quality and security.',
                },
                {
                  icon: 'ri-database-2-line',
                  title: 'Data Security Principles',
                  desc: 'We adhere to industry-standard data security principles including encryption, access controls, and regular security assessments to protect your information.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className={`${item.icon} text-accent-600`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-1.5">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Article CTA Section */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              Want to Learn More About VigorBuy Spreadsheet?
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto mb-8">
              Dive deep into our comprehensive guides covering everything from getting started to advanced techniques.
              Our original, in-depth content is designed to help you master spreadsheet management.
            </p>
            <Link
              to="/blog/what-is-vigorbuy-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-primary-500 text-white text-base font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Read the Complete Guide
              <i className="ri-book-open-line"></i>
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}